import type { ReactNode } from "react";
import { getListingProviderType } from "@wags/catalog/pure.ts";
import type { Review } from "@/components/shared/ReviewsSection";
import {
    type CollectionWagsListing,
    joinParts,
    type ListingData,
    type MediaPanelEndSlide,
} from "@/features/listings";
import { ListingReviewsPeek } from "@/features/providers";
import { MoreDogsBreederCard } from "./more-dogs-breeder-card";
import { MoreDogsGalleryEnd } from "./more-dogs-gallery-end";
import { MoreDogsGalleryThumbnail } from "./more-dogs-gallery-thumbnail";
import { MoreDogsGrid } from "./more-dogs-grid";
import {
    type ProviderOtherDogs,
    useProviderOtherDogs,
} from "./use-provider-other-dogs";

type MoreDogsInput = {
    dog: CollectionWagsListing;
    listing: Pick<ListingData, "city" | "distanceLabel"> | null | undefined;
    dogName: string;
    listingMode: boolean;
    afterIdle: boolean;
    providerName: string | undefined;
    providerAvatarUrl: string | undefined;
    providerKind: "breeder" | "shelter";
    reviews: { total: number; reviews: Review[] } | null;
};

export type MoreDogs = {
    endSlide: MediaPanelEndSlide | undefined;
    providerCard: ReactNode;
    otherDogsSection: ReactNode;
};

const NO_MORE_DOGS: MoreDogs = {
    endSlide: undefined,
    providerCard: null,
    otherDogsSection: null,
};

function providerFor(input: MoreDogsInput, providerId: string) {
    return {
        id: providerId,
        name: input.providerName ?? "This provider",
        place: joinParts([input.listing?.city, input.listing?.distanceLabel]),
        avatarUrl: input.providerAvatarUrl,
        isVerified: input.dog.organization?.isVerified === true,
        type: getListingProviderType(
            input.dog.providerType,
            input.dog.organization?.type,
        ),
    };
}

function availableSurfaces(
    input: MoreDogsInput,
    providerId: string,
    otherDogs: ProviderOtherDogs,
): MoreDogs {
    const { dog, dogName, providerName, reviews } = input;
    const provider = providerFor(input, providerId);
    const ready = otherDogs.status === "ready" ? otherDogs : null;
    const galleryTitle = providerName
        ? `More from ${providerName}`
        : "Their other dogs";
    return {
        endSlide: ready
            ? {
                  label: galleryTitle,
                  content: (
                      <MoreDogsGalleryEnd
                          title={galleryTitle}
                          dogs={ready.dogs}
                          availableOthers={ready.availableOthers}
                          providerId={providerId}
                          sourceListingId={dog.id}
                      />
                  ),
                  thumbnail: <MoreDogsGalleryThumbnail dogs={ready.dogs} />,
              }
            : undefined,
        providerCard: (
            <>
                <MoreDogsBreederCard
                    provider={provider}
                    otherDogs={otherDogs}
                    current={{
                        name: dogName,
                        photoUrl: dog.imageUrls[0],
                        providerKind: input.providerKind,
                    }}
                    sourceListingId={dog.id}
                    surface="family_row"
                />
                {reviews ? (
                    <ListingReviewsPeek
                        reviewCount={reviews.total}
                        reviews={reviews.reviews}
                        organizationId={providerId}
                    />
                ) : null}
            </>
        ),
        otherDogsSection: null,
    };
}

export function useMoreDogs(input: MoreDogsInput): MoreDogs {
    const otherDogs = useProviderOtherDogs(
        input.dog,
        input.listingMode && input.afterIdle,
    );
    const providerId = input.dog.organizationId;
    if (!input.listingMode || !providerId) return NO_MORE_DOGS;
    if (input.dog.status === "available")
        return availableSurfaces(input, providerId, otherDogs);
    return {
        ...NO_MORE_DOGS,
        otherDogsSection: (
            <MoreDogsGrid
                providerId={providerId}
                providerKind={input.providerKind}
                otherDogs={otherDogs}
                sourceListingId={input.dog.id}
            />
        ),
    };
}
